import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Box, Button, Dialog, DialogActions, DialogContent, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import TopBar from "../../../components/TopBar";
import BottomBar from "../../../components/BottomBar";
import ProductDownload from "../../../components/ProductDownload";
import ProductHero from "../../../components/Products/NAMUniversalII/ProductHero";
import ProductSections from "../../../components/Products/NAMUniversalII/ProductSections";
import interfaceImage from "../../../static/images/nam_universal_ii/nam-universal-ii-interface.webp";
import "../../../components/Products/NAMUniversalII/namUniversalII.scss";

const PRODUCT_ID = "nam-universal-ii";

const EarlyBirdPrompt = ({ open, onClose, onInterested }) => (
  <Dialog
    aria-describedby="nam-universal-ii-early-bird-description"
    aria-labelledby="nam-universal-ii-early-bird-title"
    maxWidth="xs"
    onClose={onClose}
    open={open}
    PaperProps={{
      sx: {
        background: "linear-gradient(145deg, rgb(43, 43, 43) 0%, rgb(20, 23, 25) 100%)",
        border: "1px solid rgba(249, 245, 238, 0.13)",
        borderRadius: 3,
        boxShadow: "0 24px 70px rgba(0, 0, 0, 0.58)",
        color: "rgb(249, 245, 238)",
        m: 2,
        overflow: "visible",
        position: "relative",
      },
    }}
  >
    <IconButton
      aria-label="Close early bird invitation"
      onClick={onClose}
      sx={{
        color: "rgba(249, 245, 238, 0.68)",
        position: "absolute",
        right: 8,
        top: 8,
        zIndex: 1,
        "&:hover": {
          backgroundColor: "rgba(249, 245, 238, 0.08)",
          color: "rgb(249, 245, 238)",
        },
      }}
    >
      <CloseIcon fontSize="small" />
    </IconButton>

    <DialogContent sx={{ p: { xs: 3, sm: 4 }, pb: 2.5, textAlign: "center" }}>
      <Box aria-hidden="true" sx={{ fontSize: "2rem", mb: 1 }}>
        &#128064;
      </Box>
      <Typography id="nam-universal-ii-early-bird-title" variant="h5" sx={{ fontWeight: 650, pr: 2 }}>
        Psst... interested in an early bird version?
      </Typography>
      <Typography
        id="nam-universal-ii-early-bird-description"
        sx={{ color: "rgba(249, 245, 238, 0.72)", lineHeight: 1.6, mt: 1.5 }}
      >
        NAM Universal II is still being polished, but you can already take an early build for a spin.
      </Typography>
    </DialogContent>

    <DialogActions sx={{ flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1, px: { xs: 3, sm: 4 }, pb: { xs: 3, sm: 4 } }}>
      <Button onClick={onClose} sx={{ color: "rgba(249, 245, 238, 0.72)", textTransform: "none", width: { xs: "100%", sm: "auto" } }}>
        Maybe later
      </Button>
      <Button onClick={onInterested} variant="contained" sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" } }}>
        Yes, let me try it
      </Button>
    </DialogActions>
  </Dialog>
);

const EarlyBirdBubble = ({ onClick }) => (
  <Button
    aria-label="Open the NAM Universal II early bird invitation"
    onClick={onClick}
    title="NAM Universal II early bird build"
    sx={{
      alignItems: "center",
      background: "linear-gradient(145deg, rgb(43, 43, 43) 0%, rgb(20, 23, 25) 100%)",
      border: "1px solid rgba(249, 245, 238, 0.2)",
      borderRadius: "999px",
      bottom: { xs: 16, sm: 24 },
      boxShadow: "0 10px 30px rgba(0, 0, 0, 0.42)",
      color: "rgb(249, 245, 238)",
      fontWeight: 650,
      gap: 1,
      minWidth: 0,
      p: { xs: 1.25, sm: "10px 16px" },
      position: "fixed",
      right: { xs: 16, sm: 24 },
      textTransform: "none",
      transition: "transform 160ms ease, border-color 160ms ease, background 160ms ease",
      zIndex: 1200,
      "&:hover": {
        background: "linear-gradient(145deg, rgb(55, 55, 55) 0%, rgb(26, 29, 31) 100%)",
        borderColor: "rgba(249, 245, 238, 0.38)",
        transform: "translateY(-2px)",
      },
    }}
  >
    <Box component="span" sx={{ fontSize: "1.35rem", lineHeight: 1 }}>
      &#128064;
    </Box>
    <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
      Early bird?
    </Box>
  </Button>
);

const NAMUniversalIIPage = () => {
  const [earlyBirdOpen, setEarlyBirdOpen] = useState(false);
  const [earlyBirdMinimized, setEarlyBirdMinimized] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);

  useEffect(() => {
    const earlyBirdTimer = window.setTimeout(() => setEarlyBirdOpen(true), 900);

    return () => window.clearTimeout(earlyBirdTimer);
  }, []);

  const handleInterested = () => {
    setEarlyBirdOpen(false);
    setEarlyBirdMinimized(false);
    setDownloadOpen(true);
  };

  const handleEarlyBirdDismiss = () => {
    setEarlyBirdOpen(false);
    setEarlyBirdMinimized(true);
  };

  const handleEarlyBirdRestore = () => {
    setEarlyBirdMinimized(false);
    setEarlyBirdOpen(true);
  };

  return (
    <div className="nam-universal-ii">
      <Helmet>
        <title>WaveMind Audio | NAM Universal II</title>
        <meta
          name="description"
          content="NAM Universal II is a complete guitar and bass signal chain built around Neural Amp Modeler capture playback."
        />
        <meta name="robots" content="noindex,nofollow,noarchive" />
        <link rel="preload" as="image" href={interfaceImage} />
      </Helmet>
      <TopBar menuValue="1" />
      <EarlyBirdPrompt
        onClose={handleEarlyBirdDismiss}
        onInterested={handleInterested}
        open={earlyBirdOpen}
      />
      {earlyBirdMinimized && <EarlyBirdBubble onClick={handleEarlyBirdRestore} />}
      <ProductDownload
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        productID={PRODUCT_ID}
        requireEmail
      />
      <main>
        <ProductHero />
        <ProductSections />
      </main>
      <BottomBar />
    </div>
  );
};

export default NAMUniversalIIPage;
